/**
 * El vínculo entre una persona y su número de WhatsApp, visto desde el portal.
 *
 * El portal no conoce el número: sabe **si** hay uno vinculado y en qué termina,
 * que es lo que hace falta para reconocerlo —«el que acaba en 4567»— sin tener
 * que guardarlo ni enseñarlo en una pantalla que alguien puede estar compartiendo.
 * El número entero vive del lado del servidor, donde hace de identidad.
 */
export interface WhatsAppLinkStatus {
  readonly linked: boolean;
  /** Los últimos dígitos del número vinculado. `null` si no hay ninguno. */
  readonly numberTail: string | null;
  readonly linkedAt: Date | null;
}

/**
 * El código de un solo uso con que se vincula un número.
 *
 * Se pide con la sesión de Google abierta y se manda desde el teléfono: las dos
 * mitades tienen que coincidir para que el asistente sepa quién escribe.
 */
export interface WhatsAppLinkCode {
  /** Como se enseña y como se manda: `KN-7F3K9Q`. */
  readonly code: string;
  readonly expiresAt: Date;
  /** El número del asistente, sólo cifras. `null` mientras no tenga uno. */
  readonly botNumber: string | null;
  /** El enlace que abre WhatsApp con el mensaje ya escrito. `null` sin número. */
  readonly deepLink: string | null;
}

/** El número del asistente como se lee: «+52 1 55 1234 5678» en vez de trece cifras seguidas. */
export const formatBotNumber = (digits: string): string => {
  const clean = digits.replace(/\D/g, '');
  if (clean.length <= 10) return clean;
  const local = clean.slice(-10);
  const prefix = clean.slice(0, -10);
  return `+${prefix} ${local.slice(0, 2)} ${local.slice(2, 6)} ${local.slice(6)}`;
};
